import * as THREE from 'three';

import type { LoadedCharacterMesh } from '@cartwhl/3d/gltf/types';
import {
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';
import {
  coerceOpaqueBlendMaterials,
  stripMorphTargetsFromScene,
} from '@cartwhl/3d/gltf/assetHygiene';
import { fetchBonesMapWithCredentials } from '@cartwhl/3d/r3f/hooks/fetchBonesMap';
import type {
  LoadFBXMesh,
  TransformLoadedMesh,
} from '@cartwhl/3d/r3f/hooks/useCharacterMeshWithCredentials';

import {
  CartographerFBXLoader,
  prepareCartographerFbxScene,
} from '../Cartographer/lib/fbx';

async function resolveFbxBuffer(
  meshPath: string,
  meshFile?: File | null,
): Promise<ArrayBuffer> {
  if (meshFile) {
    return meshFile.arrayBuffer();
  }

  const response = await fetch(meshPath);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.arrayBuffer();
}

function buildLoadedCharacterMesh(
  scene: THREE.Group,
  bonesMap: LoadedCharacterMesh['bonesMap'],
): LoadedCharacterMesh {
  const nodes: LoadedCharacterMesh['nodes'] = {};
  const materials: LoadedCharacterMesh['materials'] = {};
  const meshes: LoadedCharacterMesh['meshes'] = {};

  scene.traverse((object: THREE.Object3D) => {
    if (object.name) nodes[object.name] = object;

    const mesh = object as THREE.Mesh;
    if (mesh.material) {
      const material = Array.isArray(mesh.material)
        ? mesh.material[0]
        : mesh.material;
      if (material?.name) materials[material.name] = material;
    }

    if (object instanceof THREE.Mesh && object.name) {
      meshes[object.name] = object;
    }
  });

  const result: LoadedCharacterMesh = {
    scene,
    nodes,
    materials,
    meshes,
    bonesMap,
  };

  hideMeshMaterials(result);
  turnOnShadows(result);
  disableFrustumCulled(result);

  return result;
}

export function createLoadFbxCharacterMesh(meshFile?: File | null): LoadFBXMesh {
  return async (path) => {
    const buffer = await resolveFbxBuffer(path, meshFile);
    const loader = new CartographerFBXLoader(undefined, {
      ignoreMorphTargets: true,
      lodPolicy: 'highest',
    });
    const scene = loader.parse(buffer, '') as THREE.Group;

    await prepareCartographerFbxScene(scene, buffer);
    scene.userData.cartographerFbxPrepared = true;

    const bonesMap = meshFile ? {} : await fetchBonesMapWithCredentials(path);
    return buildLoadedCharacterMesh(scene, bonesMap);
  };
}

export function createFbxCharacterMeshTransform(options: {
  meshPath: string;
  meshFile?: File | null;
}): TransformLoadedMesh {
  const { meshPath, meshFile } = options;

  return async (meshData) => {
    if (!(meshData.scene instanceof THREE.Group)) return;

    if (!meshData.scene.userData.cartographerFbxPrepared) {
      try {
        const buffer = await resolveFbxBuffer(meshPath, meshFile);
        await prepareCartographerFbxScene(meshData.scene, buffer);
        meshData.scene.userData.cartographerFbxPrepared = true;
      } catch (error) {
        console.warn(
          '[FBX] Character mesh material correction skipped:',
          error,
        );
      }
    }

    coerceOpaqueBlendMaterials(meshData.scene);
    stripMorphTargetsFromScene(meshData.scene);
  };
}

/** Cartographer FBX loader for Player surfaces (presigned URLs, material prep). */
export const cartographerFbxCharacterMeshLoader: LoadFBXMesh =
  createLoadFbxCharacterMesh();
